import { api_ } from '@shared/plugIn/axios';

/**
 * 운영 계정(Admin) 로그인 2단계 인증.
 * 백엔드 스펙: smartbugo-api/docs/admin-login-phone-verification.md
 *
 * 비밀번호가 맞아도 Admin 계정에는 토큰이 바로 나가지 않는다. 대신 challenge가 오고,
 * 등록된 번호로 받은 6자리를 확인해야 그때 토큰이 발급된다.
 * 인증한 기기(x-login-device-id)는 30일간 다시 묻지 않는다.
 */

//* 200인데 토큰이 없는 응답의 표식
export const PHONE_VERIFICATION_REQUIRED = 'PHONE_VERIFICATION_REQUIRED';

export interface LoginPhoneChallenge {
  phoneVerificationRequired: true;
  code?: string;
  /** 10분짜리 JWT. send·confirm 에 그대로 실어 보낸다 */
  challenge: string;
  /** 010-****-9530 */
  phoneMasked: string;
  expiresInSec: number;
}

/**
 * 로그인 응답이 "휴대폰 인증 필요"인지.
 *
 * 구서버(이 기능 배포 전)는 항상 { token } 만 주므로 false가 되어 기존 흐름 그대로다.
 */
export const isLoginPhoneChallenge = (data: unknown): data is LoginPhoneChallenge => {
  const body = data as Partial<LoginPhoneChallenge> | null | undefined;
  return (
    !!body &&
    body.phoneVerificationRequired === true &&
    typeof body.challenge === 'string' &&
    body.challenge.length > 0
  );
};

export interface LoginPhoneSendResult {
  verificationId: string;
  /** 인터셉터(handleAxiosDates)가 Date로 바꿔 놓는다 — 문자열로 쓰지 말 것 */
  expiresAt: Date | string;
  phoneMasked: string;
}

/**
 * 등록된 번호로 인증코드 발송.
 * 번호는 클라이언트가 보내지 않는다 (서버가 계정에서 읽어 쓴다).
 */
export const sendLoginPhoneCode = async (
  challenge: string,
): Promise<LoginPhoneSendResult> => {
  const { data } = await api_.post<{ data: LoginPhoneSendResult }>(
    '/auth/login/phone-verification/send',
    { challenge },
  );
  return data.data;
};

export interface LoginPhoneConfirmResult {
  token: string;
  /** 기기 ID를 못 실었으면 false — 다음 로그인에 또 인증한다 */
  deviceRemembered: boolean;
  deviceRememberedUntil: Date | string | null;
}

/** 인증코드 확인 → 로그인 토큰 */
export const confirmLoginPhoneCode = async (dto: {
  challenge: string;
  verificationId: string;
  code: string;
}): Promise<LoginPhoneConfirmResult> => {
  const { data } = await api_.post<LoginPhoneConfirmResult>(
    '/auth/login/phone-verification/confirm',
    dto,
  );
  return data;
};
